import React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Collapse, Button, Input, Popconfirm, Tag } from 'antd';
import RemedyEditor, { queryLanguageFor } from '../util/monaco-remedy';
import FieldsEditor from './FieldsEditor';
import { rowsToFieldsObject } from './TextPanel';
import AddRR from './AddRR';

const sameConnector = (a, b) => (a || 'remedy') === (b || 'remedy');

const { Panel } = Collapse;

export const PERSON_ATTRIBUTES = ['first', 'last', 'fullName', 'email', 'phone', 'cell', 'login'];

const mapToRows = (obj, valueKey) =>
    Object.keys(obj || {}).map((field) => ({ field, [valueKey]: obj[field] }));

const rowsToMap = (rows, valueKey) => {
    const map = {};
    rows.forEach((r) => {
        if (r.field) map[r.field] = r[valueKey] !== undefined ? r[valueKey] : '';
    });
    return map;
};

/**
 * Editable panel for a personConfig entry incl. related records.
 */
const PersonPanel = ({ entry, formFields, allFields, forms, formsByConnector, connectors, onNeedFields, onNeedConnectorForms, onChange, onDelete }) => {
    const [showAddRR, setShowAddRR] = React.useState(false);
    const ql = queryLanguageFor(connectors, entry.connector);

    const personRows = Object.keys(entry.fields || {}).map((field) => ({ field, ...entry.fields[field] }));
    const relatedRecords = entry.relatedRecords || [];

    const updateRR = (i, newRR) => {
        onChange({ ...entry, relatedRecords: relatedRecords.map((rr, idx) => (idx === i ? newRR : rr)) });
    };
    const removeRR = (i) => {
        onChange({ ...entry, relatedRecords: relatedRecords.filter((_, idx) => idx !== i) });
    };

    const deleteExtra = (onConfirm, title) => (
        <Popconfirm
            title={title}
            onConfirm={(e) => { e.stopPropagation(); onConfirm(); }}
            onCancel={(e) => e.stopPropagation()}
        >
            <DeleteOutlined onClick={(event) => event.stopPropagation()} />
        </Popconfirm>
    );

    return (
        <div className="sc-entry" style={{marginBottom: 16}}>
        <Collapse ghost defaultActiveKey={['1']}>
            <Panel key={1} header={<span><span className="etype" style={{fontWeight: 680}}>{entry.form}</span><Tag color="geekblue" style={{marginLeft: 8}}>{entry.connector || "remedy"}</Tag></span>} extra={deleteExtra(onDelete, `Remove ${entry.form} from configuration?`)}>
                <div style={{fontSize: 12, color: "var(--sc-ink-3)", marginBottom: 4}}>
                    Query ({ql.name}){ql.example && <span style={{marginLeft: 8, color: "#bfbfbf"}}>e.g. {ql.example}</span>}
                </div>
                <RemedyEditor
                    key={'person-bq-editor'}
                    queryLanguage={ql}
                    minimap={{ enabled: false }}
                    height={'60px'}
                    index={'person-bq-editor'}
                    defaultValue={entry.query}
                    formFields={formFields}
                    form={entry.form}
                    onChange={(v) => onChange({ ...entry, query: v })}
                />
                <FieldsEditor
                    title={'Person Settings'}
                    rows={personRows}
                    formFields={formFields}
                    columns={[
                        { key: 'field', title: 'Field', type: 'field' },
                        { key: 'action', title: 'Action', type: 'select', options: ['random', 'clear', 'constant'] },
                        { key: 'size', title: 'Size', type: 'number', width: 100 },
                        { key: 'personAttribute', title: 'Person Attribute', type: 'select', options: PERSON_ATTRIBUTES },
                        { key: 'value', title: 'Value', type: 'text' }
                    ]}
                    onChange={(newRows) => onChange({ ...entry, fields: rowsToFieldsObject(newRows) })}
                />
                <div style={{margin: "12px 0"}}>
                    <Button icon={<PlusOutlined />} type="dashed" onClick={() => setShowAddRR(true)}>
                        add related record
                    </Button>
                </div>
                {relatedRecords.map((rr, i) => {
                    const rrConnector = rr.connector || entry.connector;
                    const found = allFields && allFields.find((af) => af.form === rr.form && sameConnector(af.connector, rrConnector));
                    const rrFields = found;
                    return (
                        <Collapse defaultActiveKey={[]} key={'Collapse-person-rr-' + i} style={{marginBottom: 8}}>
                            <Panel
                                key={`rr-${i}`}
                                header={
                                    <span>
                                        {rr.form}
                                        <Tag color="geekblue" style={{marginLeft: 8}}>{rr.connector || entry.connector || "remedy"}</Tag>
                                        {rr.description && <span style={{marginLeft: 10, fontSize: 12, color: "var(--sc-ink-3)"}}>{rr.description}</span>}
                                    </span>
                                }
                                extra={deleteExtra(() => removeRR(i), `Remove related record ${rr.form}?`)}
                            >
                                <div style={{fontSize: 12, color: "var(--sc-ink-3)", marginBottom: 4}}>Description</div>
                                <Input
                                    value={rr.description}
                                    style={{ marginBottom: 8 }}
                                    onChange={(e) => updateRR(i, { ...rr, description: e.target.value })}
                                />
                                <div style={{fontSize: 12, color: "var(--sc-ink-3)", margin: "8px 0 4px"}}>{"Query (${personId} / ${loginId} available)"}</div>
                                <RemedyEditor
                                    key={'rr' + i + '-bq-editor'}
                                    queryLanguage={queryLanguageFor(connectors, rrConnector)}
                                    height={'60px'}
                                    index={'rr' + i + '-bq-editor'}
                                    defaultValue={rr.query}
                                    formFields={rrFields && rrFields.fields}
                                    form={rr.form}
                                    minimap={{ enabled: false }}
                                    onChange={(v) => updateRR(i, { ...rr, query: v })}
                                />
                                <FieldsEditor
                                    title={'Attribute Mapping'}
                                    rows={mapToRows(rr.fields, 'attribute')}
                                    formFields={rrFields && rrFields.fields}
                                    columns={[
                                        { key: 'field', title: 'Field', type: 'field' },
                                        { key: 'attribute', title: 'Attribute', type: 'select', options: PERSON_ATTRIBUTES }
                                    ]}
                                    onChange={(rows) => updateRR(i, { ...rr, fields: rowsToMap(rows, 'attribute') })}
                                />
                                <FieldsEditor
                                    title={'Constants'}
                                    rows={mapToRows(rr.constants, 'value')}
                                    formFields={rrFields && rrFields.fields}
                                    columns={[
                                        { key: 'field', title: 'Field', type: 'field' },
                                        { key: 'value', title: 'Value', type: 'text' }
                                    ]}
                                    onChange={(rows) => updateRR(i, { ...rr, constants: rowsToMap(rows, 'value') })}
                                    addLabel={'add constant'}
                                />
                            </Panel>
                        </Collapse>
                    );
                })}
            </Panel>
            <AddRR
                open={showAddRR}
                forms={forms}
                formsByConnector={formsByConnector}
                connectors={connectors}
                defaultConnector={entry.connector}
                onConnectorChange={onNeedConnectorForms}
                onClose={() => setShowAddRR(false)}
                onAdd={(rr) => {
                    if (onNeedFields) onNeedFields(rr.form, rr.connector || entry.connector);
                    onChange({ ...entry, relatedRecords: [...relatedRecords, rr] });
                    setShowAddRR(false);
                }}
            />
        </Collapse>
        </div>
    );
};

export default PersonPanel;
